import { DatePicker, Form, Input, Modal, Select, message } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useEffect, useState } from 'react';
import { api, type EventRow, type TeamMemberRow } from '../lib/api';
import { useIsSuperAdmin } from '../lib/me';

type FormValues = {
  title: string;
  date: Dayjs | null;
  status: EventRow['status'];
  rehearsal_vmix_id: string | null;
  assigned_to: string | null;
};

export function EditEventModal({
  open,
  event,
  onClose,
  onDone,
}: {
  open: boolean;
  event: EventRow | null;
  onClose: () => void;
  onDone: () => Promise<void> | void;
}) {
  const isSuperAdmin = useIsSuperAdmin();
  const [form] = Form.useForm<FormValues>();
  const [saving, setSaving] = useState(false);
  const [members, setMembers] = useState<TeamMemberRow[]>([]);

  useEffect(() => {
    if (!open || !isSuperAdmin) return;
    void (async () => {
      try {
        setMembers(await api<TeamMemberRow[]>('/api/team-members'));
      } catch {
        setMembers([]);
      }
    })();
  }, [open, isSuperAdmin]);

  useEffect(() => {
    if (open && event) {
      form.setFieldsValue({
        title: event.title,
        date: event.date ? dayjs(event.date) : null,
        status: event.status,
        rehearsal_vmix_id: event.rehearsal_vmix_id,
        assigned_to: event.assigned_to,
      });
    }
  }, [open, event, form]);

  const onSave = async (values: FormValues) => {
    if (!event) return;
    setSaving(true);
    try {
      await api(`/api/events/${event.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          title: values.title,
          date: values.date ? values.date.format('YYYY-MM-DD') : null,
          status: values.status,
          rehearsal_vmix_id: values.rehearsal_vmix_id || null,
          ...(isSuperAdmin ? { assigned_to: values.assigned_to ?? null } : {}),
        }),
      });
      await onDone();
      onClose();
    } catch (err) {
      message.error((err as { error?: string }).error ?? 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title="Edit event"
      open={open}
      onCancel={onClose}
      onOk={() => form.submit()}
      confirmLoading={saving}
      okText="Save"
    >
      <Form form={form} layout="vertical" onFinish={onSave}>
        <Form.Item
          name="title"
          label="Title"
          rules={[{ required: true, message: 'Please enter a title' }]}
        >
          <Input autoFocus />
        </Form.Item>
        <Form.Item name="date" label="Date">
          <DatePicker style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="status" label="Status" rules={[{ required: true }]}>
          <Select
            options={[
              { value: 'draft', label: 'Draft' },
              { value: 'live', label: 'Live' },
              { value: 'ended', label: 'Ended' },
            ]}
          />
        </Form.Item>
        <Form.Item
          name="rehearsal_vmix_id"
          label="Rehearsal vMix key"
          extra="Optional. When set, the speaker's waiting room shows a 'Test mic & camera' button that opens this vMix Call key — a sandbox channel, not the broadcast feed."
        >
          <Input placeholder="e.g. AOTV-REHEARSAL-2026-06-10" allowClear />
        </Form.Item>
        {isSuperAdmin && (
          <Form.Item
            name="assigned_to"
            label="Assigned admin"
            extra="Pick which admin owns this event. Regular admins only see events assigned to them."
          >
            <Select
              allowClear
              placeholder="Unassigned (visible to every admin)"
              options={members.map((m) => ({
                value: m.id,
                label: `${m.name} · ${m.email}`,
              }))}
            />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
}
